import React from 'react';
import type { AIGenerationStage } from './AIGenerationProgressModal';
import type { Skill } from '@/services/api/agentApi';
import type { UseMissionReturn } from './MissionModal';
import type { VoiceEnrollment } from '@/services/voice/types';
import { VoiceEnrollmentDialog } from '@/components/voice/VoiceEnrollmentDialog';
import { AIGenerationProgressModal } from './AIGenerationProgressModal';
import { SkillsModal } from '@/components/agents/SkillsModal';
import { AgentsModal } from '@/components/agents/AgentsModal';
import { MissionModal } from './MissionModal';
const AgentDebugSuite = React.lazy(() => import('./AgentDebugSuite'));

export interface AIChatModalsProps {
  // Voice enrollment
  showEnrollment: boolean;
  setShowEnrollment: React.Dispatch<React.SetStateAction<boolean>>;
  onEnrollmentComplete: (enrollment: VoiceEnrollment) => void;
  // AI generation progress
  aiGenerationStage: AIGenerationStage;
  aiGenerationError: string | null;
  resetAIGenerationState: () => void;
  // Skills modal
  showSkillsModal: boolean;
  setShowSkillsModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSkillInsert: (skill: Skill) => void;
  // Agents modal
  showAgentsModal: boolean;
  setShowAgentsModal: React.Dispatch<React.SetStateAction<boolean>>;
  // Mission modal (spread)
  mission: UseMissionReturn;
  // Debug suite
  showDebugSuite: boolean;
  setShowDebugSuite: React.Dispatch<React.SetStateAction<boolean>>;
  canUsePlatformAdminDebug: boolean;
}

export const AIChatModals: React.FC<AIChatModalsProps> = ({
  showEnrollment, setShowEnrollment, onEnrollmentComplete,
  aiGenerationStage, aiGenerationError, resetAIGenerationState,
  showSkillsModal, setShowSkillsModal, handleSkillInsert,
  showAgentsModal, setShowAgentsModal,
  mission,
  showDebugSuite, setShowDebugSuite, canUsePlatformAdminDebug,
}) => {
  return (
    <>
      {/* Voice Enrollment Dialog */}
      <VoiceEnrollmentDialog
        open={showEnrollment}
        onOpenChange={setShowEnrollment}
        onComplete={onEnrollmentComplete}
      />

      {/* AI Generation Progress Modal */}
      <AIGenerationProgressModal
        isOpen={
          aiGenerationStage === 'selecting' ||
          aiGenerationStage === 'generating' ||
          aiGenerationStage === 'finalizing' ||
          aiGenerationStage === 'complete' ||
          aiGenerationStage === 'error'
        }
        currentStage={aiGenerationStage}
        error={aiGenerationError}
        onClose={() => {
          if (aiGenerationStage === 'complete' || aiGenerationStage === 'error') {
            resetAIGenerationState();
          }
        }}
      />
      {/* Skills Modal */}
      <SkillsModal
        open={showSkillsModal}
        onOpenChange={setShowSkillsModal}
        onSkillInsert={handleSkillInsert}
      />
      {/* Agents Modal */}
      <AgentsModal open={showAgentsModal} onOpenChange={setShowAgentsModal} />
      {/* Knowledge Modal is rendered globally in RootLayout */}
      {/* Mission Modal */}
      <MissionModal {...mission} />
      {/* Agent Debug Suite — platform admins only */}
      {canUsePlatformAdminDebug && showDebugSuite && (
        <React.Suspense fallback={null}>
          <AgentDebugSuite open={showDebugSuite} onOpenChange={setShowDebugSuite} />
        </React.Suspense>
      )}
    </>
  );
};
